import { motion } from 'framer-motion';
import cn from 'classnames';
import {
  Bold,
  ChevronDown,
  Eraser,
  Highlighter,
  Italic,
  Strikethrough,
  Underline
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { RefObject, useEffect, useRef, useState } from 'react';
import { useSharedTranscript } from '@utils/providers/shared-transcript-context.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { useTranslation } from 'react-i18next';
import { shortcutLabel } from '@utils/editors/shortcut-label.tsx';

/* eslint-disable no-restricted-syntax -- Semantic editor colors (recording/streaming status, playback cues, subtitle palette, pill foregrounds on tinted backgrounds) must stay stable across themes */

const HIGHLIGHT_COLORS = [
  { color: '#FEE047', label: 'highlight-yellow' },
  { color: '#F9A8D4', label: 'highlight-pink' },
  { color: '#86EFAC', label: 'highlight-green' }
] as const;

interface Props {
  containerRef: RefObject<HTMLDivElement>;
  popoverOpen: boolean | undefined;
}

export const SharedTranscriptTextStyleBox = ({
  containerRef,
  popoverOpen
}: Props) => {
  const { getColour } = useThemeContext();
  const { t } = useTranslation();
  const { applyStyle } = useSharedTranscript();
  const [lastUsedColor, setLastUsedColor] = useState<string>(
    HIGHLIGHT_COLORS[0].color
  );
  const [isHighlightDropdownOpen, setIsHighlightDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const highlightButtonRef = useRef<HTMLDivElement>(null);
  const dropdownToggleRef = useRef<HTMLButtonElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isHighlightDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideDropdown =
        dropdownMenuRef.current && !dropdownMenuRef.current.contains(target);
      const isOutsideToggle =
        dropdownToggleRef.current &&
        !dropdownToggleRef.current.contains(target);

      if (isOutsideDropdown && isOutsideToggle) {
        setIsHighlightDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isHighlightDropdownOpen]);

  const handleHighlightClick = () => {
    applyStyle('mark', lastUsedColor);
  };

  const handleColorSelect = (color: string) => {
    setLastUsedColor(color);
    applyStyle('mark', color);
    setIsHighlightDropdownOpen(false);
  };

  const handleDropdownToggle = () => {
    if (!isHighlightDropdownOpen && dropdownToggleRef.current) {
      const rect = dropdownToggleRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left - 40
      });
    }
    setIsHighlightDropdownOpen(!isHighlightDropdownOpen);
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative transition-shadow duration-300 border ',
        {
          'rounded-2xl border border-gray-200': popoverOpen
        },
        'border-transparent'
      )}
      style={{
        display: 'block',
        maxWidth: 'fit-content'
      }}
    >
      <motion.div transition={{ duration: 0.5 }} className="border-gray-200">
        <div className="flex gap-1 w-full h-9 items-center">
          <Tooltip label={shortcutLabel(t('bold'), 'Ctrl+B')}>
            <button
              onClick={() => applyStyle('bold')}
              aria-label={t('bold')}
              className="h-9 w-9 flex items-center justify-center transition-colors hover:opacity-80 rounded-2xl border shrink-0"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('thirdLayer'),
                color: getColour('icon')
              }}
            >
              <Bold size={14} aria-hidden="true" />
            </button>
          </Tooltip>
          <Tooltip label={shortcutLabel(t('italic'), 'Ctrl+I')}>
            <button
              onClick={() => applyStyle('italic')}
              aria-label={t('italic')}
              className="h-9 w-9 flex items-center justify-center transition-colors hover:opacity-80 rounded-2xl border shrink-0"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('thirdLayer'),
                color: getColour('icon')
              }}
            >
              <Italic size={14} aria-hidden="true" />
            </button>
          </Tooltip>
          <Tooltip label={shortcutLabel(t('underline'), 'Ctrl+U')}>
            <button
              onClick={() => applyStyle('underlined')}
              aria-label={t('underline')}
              className="h-9 w-9 flex items-center justify-center transition-colors hover:opacity-80 rounded-2xl border shrink-0"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('thirdLayer'),
                color: getColour('icon')
              }}
            >
              <Underline size={14} aria-hidden="true" />
            </button>
          </Tooltip>
          <Tooltip label={shortcutLabel(t('strikethrough'), 'Ctrl+Shift+X')}>
            <button
              onClick={() => applyStyle('strike')}
              aria-label={t('strikethrough')}
              className="h-9 w-9 flex items-center justify-center transition-colors hover:opacity-80 rounded-2xl border shrink-0"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('thirdLayer'),
                color: getColour('icon')
              }}
            >
              <Strikethrough size={14} aria-hidden="true" />
            </button>
          </Tooltip>

          {/* Highlight — single split-button control: click applies last-used
              color; chevron opens color swatches + remove option. */}
          <div
            ref={highlightButtonRef}
            className="relative inline-flex items-center rounded-2xl border overflow-hidden shrink-0"
            style={{
              borderColor: getColour('border'),
              backgroundColor: getColour('thirdLayer')
            }}
          >
            <Tooltip label={t('highlight')}>
              <button
                onClick={handleHighlightClick}
                aria-label={t('highlight')}
                className="h-9 w-9 flex items-center justify-center transition-colors hover:opacity-80"
                style={{ color: getColour('icon') }}
              >
                <Highlighter
                  size={14}
                  style={{ color: lastUsedColor }}
                  aria-hidden="true"
                />
              </button>
            </Tooltip>
            <div
              className="w-px h-5"
              style={{ backgroundColor: getColour('border') }}
            />
            <Tooltip label={t('more-colors')}>
              <button
                ref={dropdownToggleRef}
                onClick={handleDropdownToggle}
                aria-label={t('more-colors')}
                aria-expanded={isHighlightDropdownOpen}
                aria-haspopup="menu"
                className="h-9 w-6 flex items-center justify-center transition-colors hover:opacity-80"
                style={{ color: getColour('icon') }}
              >
                <ChevronDown size={14} aria-hidden="true" />
              </button>
            </Tooltip>
            {isHighlightDropdownOpen &&
              createPortal(
                <div
                  ref={dropdownMenuRef}
                  className="fixed p-2 rounded-2xl border shadow-lg flex flex-col gap-1.5"
                  style={{
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    backgroundColor: getColour('thirdLayer'),
                    borderColor: getColour('border'),
                    zIndex: 9999
                  }}
                >
                  <div className="flex gap-1.5 px-0.5">
                    {HIGHLIGHT_COLORS.map(({ color, label }) => (
                      <Tooltip key={color} label={t(label)}>
                        <button
                          onClick={() => handleColorSelect(color)}
                          className={cn(
                            'w-5 h-5 rounded-full transition-all duration-200 cursor-pointer hover:scale-110',
                            lastUsedColor === color &&
                              'ring-2 ring-offset-1 ring-gray-200'
                          )}
                          style={{
                            backgroundColor: color,
                            border: `1px solid ${getColour('border')}`
                          }}
                        />
                      </Tooltip>
                    ))}
                  </div>
                  <div
                    className="h-px w-full"
                    style={{ backgroundColor: getColour('border') }}
                  />
                  <button
                    onClick={() => {
                      applyStyle('unmark');
                      setIsHighlightDropdownOpen(false);
                    }}
                    className="flex items-center gap-2 px-2 py-1 rounded-lg text-xs transition-colors hover:opacity-80"
                    style={{ color: getColour('normalText') }}
                  >
                    <Eraser size={14} style={{ color: getColour('icon') }} />
                    {t('remove-highlight')}
                  </button>
                </div>,
                document.body
              )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};
